import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Handshake, ExternalLink, Sparkles, Loader2, Send, MoreVertical,
  MessageCircle, Calendar, CheckCircle2, XCircle, TrendingUp, Euro,
} from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Platform {
  id: string;
  name: string;
  country: string;
  type: string;
  website: string;
  contact: string;
  description: string;
  sweet_spot: string;
  model: string;
}

type PlatStatus = "sent" | "replied" | "call" | "signed" | "rejected";

interface PipelineRow {
  platform_id: string;
  status: PlatStatus;
  notes?: string;
}

interface ClosersKpi {
  sent: number;
  replied: number;
  call: number;
  signed: number;
  rejected: number;
}

const STATUS_META: Record<PlatStatus, { label: string; className: string; icon: React.ComponentType<{ className?: string }> }> = {
  sent:     { label: "Envoyé",      className: "text-sky-400 border-sky-500/30 bg-sky-500/10", icon: Send },
  replied:  { label: "Répondu",     className: "text-amber-400 border-amber-500/30 bg-amber-500/10", icon: MessageCircle },
  call:     { label: "Call booké",  className: "text-violet-400 border-violet-500/30 bg-violet-500/10", icon: Calendar },
  signed:   { label: "Partenariat signé", className: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", icon: CheckCircle2 },
  rejected: { label: "Rejeté",      className: "text-rose-400 border-rose-500/30 bg-rose-500/10", icon: XCircle },
};

export default function ClosersSection() {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailCfg, setEmailCfg] = useState<{ configured: boolean } | null>(null);
  const [kpi, setKpi] = useState<ClosersKpi | null>(null);
  const [statusByPlat, setStatusByPlat] = useState<Record<string, PipelineRow>>({});
  const [selected, setSelected] = useState<Platform | null>(null);
  const [note, setNote] = useState("");
  const [commission, setCommission] = useState(15);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState<{ action_id: string; subject: string; body: string; to: string; needs_recipient?: boolean; suggested_contact_channel?: string | null } | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const loadedOnce = useRef(false);

  const loadPipeline = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/closers/pipeline`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      setKpi(d.kpi);
      setStatusByPlat(d.by_platform || {});
    } catch { /* tolerate */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [pRes, eRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/closers/platforms`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/emails/status`, { credentials: "include" }),
        ]);
        const pData = await pRes.json();
        const eData = await eRes.json();
        setPlatforms(pData.platforms || []);
        setEmailCfg(eData);
      } catch (e) { console.error(e); }
      await loadPipeline();
      loadedOnce.current = true;
      setLoading(false);
    })();
  }, []);

  const setPlatStatus = async (p: Platform, status: PlatStatus) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/closers/pipeline/${p.id}/status`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error();
      await loadPipeline();
      toast.success(`${p.name} → ${STATUS_META[status].label}`);
    } catch {
      toast.error("Échec mise à jour du statut");
    }
  };

  const openDraftDialog = (p: Platform) => {
    setSelected(p);
    setDraft(null);
    setNote("");
    setRecipientEmail("");
    setCommission(15);
  };

  const generateDraft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/closers/intro-draft`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform_id: selected.id,
          note,
          commission_pct: commission,
          recipient_email: recipientEmail.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setDraft({
        action_id: d.action_id,
        subject: d.draft.subject,
        body: d.draft.body,
        to: d.draft.to,
        needs_recipient: d.needs_recipient,
        suggested_contact_channel: d.suggested_contact_channel,
      });
      toast.success("Email de recrutement préparé");
    } catch {
      toast.error("Erreur de génération");
    }
    setDrafting(false);
  };

  const updateRecipient = async () => {
    if (!draft || !recipientEmail.trim()) return;
    try {
      await fetch(`${BACKEND_URL}/api/orchestrator/actions/${draft.action_id}/set-recipient`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: recipientEmail.trim() }),
      });
      setDraft(d => d ? { ...d, to: recipientEmail.trim(), needs_recipient: false } : null);
    } catch { /* tolerated */ }
  };

  const sendReal = async () => {
    if (!draft || !selected) return;
    if (!emailCfg?.configured) {
      toast.error("Connecte Gmail dans l'onglet Messagerie pour envoyer pour de vrai");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/emails/send/${draft.action_id}`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        toast.success(`Email envoyé à ${selected.name}`);
        await loadPipeline();
        setSelected(null);
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Envoi échoué");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setSending(false);
  };

  const replyRate = kpi && kpi.sent > 0 ? Math.round((kpi.replied / kpi.sent) * 100) : 0;
  const kpiItems = [
    { key: "sent",    label: "Contactés",     value: kpi?.sent ?? 0,    icon: Send,          color: "text-sky-400" },
    { key: "replied", label: "Répondus",      value: kpi?.replied ?? 0, icon: MessageCircle, color: "text-amber-400" },
    { key: "call",    label: "Calls bookés",  value: kpi?.call ?? 0,    icon: Calendar,      color: "text-violet-400" },
    { key: "signed",  label: "Partenariats",  value: kpi?.signed ?? 0,  icon: CheckCircle2,  color: "text-emerald-400" },
  ];

  return (
    <div className="space-y-3">
      {/* KPI strip */}
      <Card className="bg-card border-border" data-testid="closers-pipeline-kpi">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Pipeline Closers — recrutement en cours
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
              <span>Taux de réponse : <b className="text-foreground">{replyRate}%</b></span>
              <span className="flex items-center gap-1">
                <Euro className="h-3 w-3" /> Commission standard : <b className="text-primary">15%</b>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            {kpiItems.map(it => (
              <div key={it.key} className="border border-border rounded-lg p-3 flex items-center gap-3 bg-background/30" data-testid={`closers-kpi-${it.key}`}>
                <div className={`w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center ${it.color}`}>
                  <it.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{it.label}</p>
                  <p className="text-xl font-bold leading-none mt-0.5">{it.value}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Platforms grid */}
      <Card className="bg-card border-border" data-testid="closers-section">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Handshake className="h-4 w-4 text-primary" />
                Plateformes de closers & agences commerciales
              </CardTitle>
              <CardDescription>
                {platforms.length} plateformes curées — FR, US, Global · Commission 15% sur contrats signés · Ton pipe de closing automatisé
              </CardDescription>
            </div>
            {emailCfg && (
              <Badge variant="outline" className={`text-[10px] ${emailCfg.configured ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`} data-testid="closers-email-badge">
                {emailCfg.configured ? "Envoi réel actif" : "Envoi non configuré"}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="closers-grid">
              {platforms.map((p) => {
                const row = statusByPlat[p.id];
                const StatusIcon = row ? STATUS_META[row.status].icon : null;
                return (
                  <div key={p.id} className="group border border-border rounded-lg p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors flex flex-col" data-testid={`closer-${p.id}`}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-semibold">{p.name}</span>
                          {row && StatusIcon && (
                            <Badge variant="outline" className={`text-[9px] ${STATUS_META[row.status].className}`} data-testid={`closer-status-${p.id}`}>
                              <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
                              {STATUS_META[row.status].label}
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground">{p.country} · {p.type}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={p.website} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                        </a>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-0.5 rounded hover:bg-secondary" data-testid={`closer-menu-${p.id}`}>
                              <MoreVertical className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="text-[10px] text-muted-foreground">Changer le statut</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => setPlatStatus(p, "sent")}>
                              <Send className="h-3.5 w-3.5 mr-2 text-sky-400" /> Envoyé
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPlatStatus(p, "replied")}>
                              <MessageCircle className="h-3.5 w-3.5 mr-2 text-amber-400" /> Répondu
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPlatStatus(p, "call")}>
                              <Calendar className="h-3.5 w-3.5 mr-2 text-violet-400" /> Call booké
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPlatStatus(p, "signed")}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-emerald-400" /> Partenariat signé
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setPlatStatus(p, "rejected")}>
                              <XCircle className="h-3.5 w-3.5 mr-2 text-rose-400" /> Rejeté
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground my-2 line-clamp-2">{p.description}</p>
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      <Badge variant="outline" className="text-[9px] text-primary">{p.model}</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mb-3">
                      <b>Sweet spot:</b> {p.sweet_spot}
                    </p>
                    <Button size="sm" variant="outline" className="gap-1 mt-auto" onClick={() => openDraftDialog(p)} data-testid={`btn-recruit-${p.id}`}>
                      <Sparkles className="h-3 w-3" /> Recruter — 15%
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>

        {/* Draft dialog */}
        <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl" data-testid="closer-intro-dialog">
            <DialogHeader>
              <DialogTitle>
                Recruter <span className="text-primary">{selected?.name}</span>
              </DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/40 text-xs space-y-1">
                  <p><b>Type</b> : {selected.type} · {selected.country}</p>
                  <p><b>Sweet spot</b> : {selected.sweet_spot}</p>
                  <p><b>Modèle</b> : {selected.model}</p>
                  <p><b>Contact</b> : {selected.contact}</p>
                </div>
                {!draft ? (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">Commission (%) :</label>
                      <Input
                        type="number"
                        value={commission}
                        onChange={(e) => setCommission(parseInt(e.target.value) || 15)}
                        className="w-20 h-8 text-xs"
                        data-testid="commission-input"
                        min={1} max={50}
                      />
                      <span className="text-[10px] text-muted-foreground">par contrat signé</span>
                    </div>
                    <Textarea
                      placeholder="Note optionnelle (contexte, traction, ce qui rend l'offre attractive pour leur pool de closers…)"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      data-testid="closer-note"
                    />
                    <Button onClick={generateDraft} disabled={drafting} className="gap-1.5 w-full" data-testid="generate-closer-btn">
                      {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Générer l'email de recrutement avec Grafton
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="p-3 rounded-lg bg-background border border-border space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground shrink-0">À :</span>
                        {draft.needs_recipient ? (
                          <div className="flex gap-1 flex-1">
                            <Input
                              type="email"
                              placeholder={draft.suggested_contact_channel ? `Trouvé : ${draft.suggested_contact_channel}` : "email@closerplatform.com"}
                              value={recipientEmail}
                              onChange={(e) => setRecipientEmail(e.target.value)}
                              className="h-7 text-xs flex-1"
                              data-testid="closer-recipient-input"
                            />
                            <Button size="sm" variant="outline" className="h-7" onClick={updateRecipient} disabled={!recipientEmail.trim()}>Valider</Button>
                          </div>
                        ) : (
                          <b className="text-foreground">{draft.to}</b>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Sujet : <b className="text-foreground">{draft.subject}</b></p>
                      <pre className="whitespace-pre-wrap text-xs font-sans leading-relaxed bg-muted/30 p-3 rounded-md border border-border/40">{draft.body}</pre>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="gap-1.5 flex-1" onClick={async () => { const ok = await copyToClipboard(draft.body); ok ? toast.success("Copié") : toast.error("Copie bloquée — sélectionne et copie manuellement"); }} data-testid="copy-closer-btn">
                        Copier
                      </Button>
                      <Button className="gap-1.5 flex-1" onClick={sendReal} disabled={sending || !emailCfg?.configured || !draft.to} data-testid="send-closer-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {!emailCfg?.configured ? "Gmail non connecté" : !draft.to ? "Saisis un email" : "Envoyer pour de vrai"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}
